import React, { useState, useEffect } from 'react';
import { apiClient } from '../../apiClient';
import { 
    AlertTriangle, CheckCircle, Clock, Server, Shield, 
    BarChart3, RefreshCcw, ChevronLeft, ChevronRight 
} from 'lucide-react';

const FailureHistory = () => {
    const [events, setEvents] = useState([]);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        type: '',
        severity: '',
        service: '',
        from: '',
        to: ''
    });
    const [page, setPage] = useState(1);
    const limit = 50;

    const fetchMonitorData = async () => {
        try {
            const queryParams = new URLSearchParams({
                limit: limit.toString(),
                offset: ((page - 1) * limit).toString(),
                ...filters
            });

            const [eventsRes, statusRes] = await Promise.all([
                apiClient.get(`/v1/admin/monitor/events?${queryParams.toString()}`),
                apiClient.get('/v1/admin/monitor/status')
            ]);

            if (eventsRes.ok && statusRes.ok) {
                const eventsData = await eventsRes.json();
                const statusData = await statusRes.json();
                setEvents(eventsData.events || []);
                setStatus(statusData);
            }
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMonitorData();
        const interval = setInterval(fetchMonitorData, 30000); // 30s auto-refresh
        return () => clearInterval(interval);
    }, [filters, page]);

    const getStatusColor = (state) => {
        switch (state) {
            case 'healthy': return 'bg-green-100 text-green-800 border-green-200';
            case 'degraded': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'maintenance': return 'bg-red-100 text-red-800 border-red-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getSeverityColor = (sev) => {
        switch (sev) {
            case 'critical': return 'bg-red-100 text-red-700';
            case 'warning': return 'bg-yellow-100 text-yellow-700';
            case 'info': return 'bg-blue-100 text-blue-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const formatServiceName = (name) => {
        const names = {
            api: 'API Endpoint',
            firestoreRead: 'Firestore (R)',
            firestoreWrite: 'Firestore (W)',
            auth: 'Authentication',
            stripe: 'Stripe API',
            functions: 'Cloud Functions',
            smtp: 'SMTP Server'
        };
        return names[name] || name;
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPage(1);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">
                    システム監視
                </h2>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <RefreshCcw size={14} className={loading ? "animate-spin" : ""} />
                    30秒ごとに自動更新
                </div>
            </div>

            {/* Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`p-6 rounded-xl border-2 shadow-sm flex items-center gap-4 ${status ? getStatusColor(status.currentState) : 'bg-gray-50'}`}>
                    {status?.currentState === 'healthy' && <CheckCircle size={40} />}
                    {status?.currentState === 'degraded' && <AlertTriangle size={40} />}
                    {status?.currentState === 'maintenance' && <Shield size={40} />}
                    <div>
                        <p className="text-xs uppercase font-bold tracking-wider opacity-70">現在のシステム状態</p>
                        <p className="text-2xl font-black">
                            {status?.currentState === 'healthy' ? 'HEALTHY' : 
                             status?.currentState === 'degraded' ? 'DEGRADED' : 
                             status?.currentState === 'maintenance' ? 'MAINTENANCE' : 'UNKNOWN'}
                        </p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
                    <Clock size={40} className="text-blue-500" />
                    <div>
                        <p className="text-xs uppercase font-bold text-gray-400">最終監視時刻</p>
                        <p className="text-lg font-bold">
                            {status?.lastHealthCheck ? new Date(status.lastHealthCheck).toLocaleTimeString('ja-JP') : '-'}
                        </p>
                        <p className="text-xs text-gray-400">
                            {status?.lastHealthCheck ? new Date(status.lastHealthCheck).toLocaleDateString('ja-JP') : ''}
                        </p>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex items-center gap-4">
                    <Shield size={40} className={status?.maintenance ? "text-red-500" : "text-gray-300"} />
                    <div>
                        <p className="text-xs uppercase font-bold text-gray-400">メンテナンス</p>
                        <p className="text-lg font-bold">{status?.maintenance ? '有効 (ACTIVE)' : '無効 (INACTIVE)'}</p>
                    </div>
                </div>
            </div>

            {/* Timestamps Panel */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm border-l-4 border-l-red-500">
                    <p className="text-xs font-bold text-gray-400 uppercase mb-1">最終障害日時</p>
                    <p className="text-sm font-bold text-gray-700">
                        {status?.lastFailure ? new Date(status.lastFailure).toLocaleString('ja-JP') : 'なし'}
                    </p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                    <p className="text-xs font-bold text-gray-400 uppercase mb-1">最終復旧日時</p>
                    <p className="text-sm font-bold text-gray-700">
                        {status?.lastRecovery ? new Date(status.lastRecovery).toLocaleString('ja-JP') : 'なし'}
                    </p>
                </div>
            </div>

            {/* Metrics Panel */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">24h 障害回数</p>
                    <p className="text-2xl font-bold text-red-600">{status?.metrics?.failureCount24h || 0}</p>
                </div>
                <div className="text-center border-l border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">24h 復旧回数</p>
                    <p className="text-2xl font-bold text-green-600">{status?.metrics?.recoveryCount24h || 0}</p>
                </div>
                <div className="text-center border-l border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">連続失敗数</p>
                    <p className="text-2xl font-bold text-yellow-600">{status?.metrics?.continuousFailures || 0}</p>
                </div>
                <div className="text-center border-l border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">平均応答時間</p>
                    <p className="text-2xl font-bold text-blue-600">180ms</p>
                </div>
            </div>

            {/* Service Status Grid */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                    <Server size={16} /> サービス別障害状況
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
                    {status?.serviceStatus && Object.entries(status.serviceStatus).map(([name, state]) => (
                        <div key={name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                            <span className="text-xs font-bold text-gray-600">{formatServiceName(name)}</span>
                            <span className={`h-3 w-3 rounded-full ${state === 'healthy' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-red-500 animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.6)]'}`}></span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Event Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b bg-gray-50 flex flex-wrap gap-4 items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-700 font-bold">
                        <BarChart3 size={18} /> 障害履歴
                    </div>
                    <div className="flex gap-2">
                        <select name="type" value={filters.type} onChange={handleFilterChange} className="text-xs border-gray-300 rounded shadow-sm">
                            <option value="">全ての種別</option>
                            <option value="health_failed">異常検知</option>
                            <option value="health_ok">正常復帰</option>
                            <option value="maintenance_enabled">メンテナンス開始</option>
                            <option value="maintenance_disabled">メンテナンス解除</option>
                        </select>
                        <select name="service" value={filters.service} onChange={handleFilterChange} className="text-xs border-gray-300 rounded shadow-sm">
                            <option value="">全てのサービス</option>
                            <option value="api">API Endpoint</option>
                            <option value="firestoreRead">Firestore Read</option>
                            <option value="firestoreWrite">Firestore Write</option>
                            <option value="auth">Auth</option>
                            <option value="stripe">Stripe</option>
                            <option value="functions">Functions</option>
                            <option value="smtp">SMTP Server</option>
                        </select>
                        <select name="severity" value={filters.severity} onChange={handleFilterChange} className="text-xs border-gray-300 rounded shadow-sm">
                            <option value="">全ての重要度</option>
                            <option value="info">INFO</option>
                            <option value="warning">WARNING</option>
                            <option value="critical">CRITICAL</option>
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">日時</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">種別</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">重要度</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">サービス</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">メッセージ</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200 text-sm">
                            {events.map((event) => (
                                <tr key={event.id} className="hover:bg-blue-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-mono text-xs">
                                        {event.timestamp ? new Date(event.timestamp).toLocaleString('ja-JP', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 rounded text-[10px] font-bold ${
                                            event.type === 'health_failed' ? 'text-red-600' : 
                                            event.type === 'health_ok' ? 'text-green-600' : 
                                            event.type === 'maintenance_enabled' ? 'text-orange-600' : 'text-blue-600'
                                        }`}>
                                            {event.type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase ${getSeverityColor(event.severity)}`}>
                                            {event.severity}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-700 font-medium text-xs">
                                        {formatServiceName(event.service) || '-'}
                                    </td>
                                    <td className="px-6 py-4 text-gray-500 max-w-xs truncate text-xs">
                                        {event.message}
                                    </td>
                                </tr>
                            ))}
                            {events.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                                        表示するイベントはありません。
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="p-4 border-t bg-gray-50 flex justify-between items-center text-xs">
                    <span className="text-gray-500">
                        Page {page}
                    </span>
                    <div className="flex gap-2">
                        <button 
                            disabled={page === 1}
                            onClick={() => setPage(p => p - 1)}
                            className="p-1 border rounded disabled:opacity-30 bg-white"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <button 
                            onClick={() => setPage(p => p + 1)}
                            className="p-1 border rounded bg-white"
                        >
                            <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FailureHistory;
